import React, { useRef, useState } from "react";
import axios from "axios";
import {
  Eye,
  EyeOff,
  CheckCircle,
  Check,
  ArrowRight,
  ArrowLeft,
  Phone,
  Mail,
  User,
  Lock,
  Building2,
  KeyRound,
  UtensilsCrossed,
  Camera,
  Palette,
} from "lucide-react";
import { API_BASE_URL } from "../utils/urls";
import { useNavigate } from "react-router-dom";
import useSEO from "../hooks/useSEO";
import AuthLayout, { fieldClass, labelClass, fieldIconClass, AuthEyebrow } from "../components/Layout/AuthLayout";

const VendorRegistration = () => {
  useSEO({
    title: "Become a Vendor",
    description:
      "Register your venue, catering, photography or decoration business on MandapAI. Free to join, no setup fees — reach customers across 500+ cities with AI growth tools.",
    path: "/vendor-registration",
  });
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    business_name: "",
    name: "",
    email: "",
    phone: "",
    password: "",
    vendorType: "venue",
  });
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [vendorData, setVendorData] = useState(null);
  const otpRefs = useRef([]);

  const vendorTypes = [
    { value: "venue", label: "Venue", icon: Building2, desc: "Halls & venues" },
    { value: "caterer", label: "Caterer", icon: UtensilsCrossed, desc: "Food & catering" },
    { value: "decorator", label: "Decorator", icon: Palette, desc: "Decor & styling" },
    { value: "photographer", label: "Photographer", icon: Camera, desc: "Photo & video" },
  ];

  const validateStep1 = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Full name is required";
    if (!formData.business_name.trim())
      newErrors.business_name = "Business name is required";
    if (!formData.email.trim()) newErrors.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(formData.email))
      newErrors.email = "Email is invalid";
    if (!formData.password) newErrors.password = "Password is required";
    else if (formData.password.length < 8)
      newErrors.password = "Password must be at least 8 characters";
    if (!formData.vendorType) newErrors.vendorType = "Please select vendor type";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleContinue = () => {
    if (validateStep1()) setStep(2);
  };

  const validatePhone = () => {
    const newErrors = {};
    if (!formData.phone.trim()) newErrors.phone = "Phone number is required";
    else if (!/^\d{10}$/.test(formData.phone))
      newErrors.phone = "Phone must be 10 digits";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSendOtp = async () => {
    if (!validatePhone()) return;
    setLoading(true);
    setErrors({});
    try {
      const response = await axios.post(`${API_BASE_URL}/vendors/register`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        password: formData.password,
        vendorType: formData.vendorType,
        business_name: formData.business_name,
      });
      if (response.data.success) {
        setVendorData({ vendorId: response.data.data.vendorId });
        setOtpSent(true);
      }
    } catch (error) {
      console.error("Registration error:", error);
      if (error.response) {
        setErrors({
          submit:
            error.response.data.message ||
            "Registration failed. Please try again.",
        });
      } else if (error.request) {
        setErrors({ submit: "Network error. Please check your connection." });
      } else {
        setErrors({ submit: "Registration failed. Please try again." });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (!formData.phone) {
      setErrors({ otp: "Phone number not found. Please restart registration." });
      return;
    }
    setLoading(true);
    setErrors({});
    try {
      const response = await axios.post(`${API_BASE_URL}/vendors/resend-otp`, {
        phone: formData.phone,
      });
      if (response.data.success) {
        setErrors({ otp: "" });
        console.log("OTP resent successfully");
      }
    } catch (error) {
      console.error("Resend OTP error:", error);
      setErrors({
        otp: error.response
          ? error.response.data.message || "Failed to resend OTP. Please try again."
          : "Network error. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp.trim()) {
      setErrors({ otp: "Please enter OTP" });
      return;
    }
    if (otp.length !== 6) {
      setErrors({ otp: "OTP must be 6 digits" });
      return;
    }
    setLoading(true);
    setErrors({});
    try {
      const response = await axios.post(`${API_BASE_URL}/vendors/verify-otp`, {
        phone: formData.phone,
        otp: otp,
      });
      if (response.data.success) {
        setVendorData(response.data.data);
        setStep(3);
      }
    } catch (error) {
      console.error("OTP verification error:", error);
      if (error.response) {
        setErrors({ otp: error.response.data.message || "Invalid OTP. Please try again." });
      } else if (error.request) {
        setErrors({ otp: "Network error. Please try again." });
      } else {
        setErrors({ otp: "Verification failed. Please try again." });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const STEP_LABELS = ["Details", "Verify", "Done"];

  const Stepper = () => (
    <div className="mb-7">
      <div className="flex items-center justify-between gap-3">
        <AuthEyebrow>Step {step} of {STEP_LABELS.length}</AuthEyebrow>
        <span className="mb-4 text-xs font-semibold text-gray-500">{STEP_LABELS[step - 1]}</span>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {STEP_LABELS.map((label, i) => {
          const n = i + 1;
          const active = step === n;
          const done = step > n;
          return (
            <div key={label}>
              <div className={`h-1.5 rounded-full transition-colors duration-300 ${done || active ? "bg-gradient-to-r from-purple-600 to-pink-600" : "bg-gray-200"}`} />
              <div className="mt-2 flex items-center gap-1.5">
                <span
                  className={`flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold ${
                    done || active ? "bg-gradient-to-br from-purple-600 to-pink-600 text-white" : "bg-gray-100 text-gray-400"
                  }`}
                >
                  {done ? <Check className="h-3 w-3" /> : n}
                </span>
                <span className={`text-xs font-semibold ${active || done ? "text-gray-900" : "text-gray-400"}`}>{label}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  const PRIMARY_BTN =
    "group flex w-full items-center justify-center gap-2 h-12 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 px-5 text-[15px] font-['Manrope'] font-semibold text-white shadow-lg shadow-purple-600/20 transition-all duration-200 hover:shadow-xl hover:shadow-purple-600/30 disabled:cursor-not-allowed disabled:opacity-70 focus:outline-none focus:ring-2 focus:ring-purple-500/40";
  const ERROR_TEXT = "mt-1.5 text-xs font-medium text-red-500";
  const LINK =
    "font-semibold text-gray-900 underline decoration-purple-300 decoration-2 underline-offset-4 transition-colors hover:decoration-purple-600";

  // Numbered section heading inside a step
  const SectionTitle = ({ n, title, hint }) => (
    <div className="mb-3 flex items-center gap-2.5">
      <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-lg bg-purple-50 text-[11px] font-bold text-gray-700 ring-1 ring-inset ring-purple-200/70">
        {n}
      </span>
      <p className="font-['Manrope'] text-sm font-bold text-gray-900">{title}</p>
      {hint && <p className="ml-auto text-xs text-gray-400">{hint}</p>}
    </div>
  );

  // ── OTP boxes: six single-digit inputs backed by the `otp` string ──
  const focusOtpBox = (i) => otpRefs.current[Math.max(0, Math.min(5, i))]?.focus();

  const handleOtpInput = (i, raw) => {
    const digits = raw.replace(/\D/g, "");
    if (!digits) return;
    const at = Math.min(i, otp.length); // never leave a gap
    const next = (otp.slice(0, at) + digits + otp.slice(at + digits.length)).slice(0, 6);
    setOtp(next);
    if (errors.otp) setErrors((prev) => ({ ...prev, otp: "" }));
    focusOtpBox(at + digits.length);
  };

  const handleOtpKeyDown = (i, e) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      const at = otp[i] ? i : i - 1;
      if (at < 0) return;
      setOtp(otp.slice(0, at));
      focusOtpBox(at);
    } else if (e.key === "ArrowLeft") {
      focusOtpBox(i - 1);
    } else if (e.key === "ArrowRight") {
      focusOtpBox(i + 1);
    } else if (e.key === "Enter" && otp.length === 6) {
      handleVerifyOtp();
    }
  };

  const selectedType = vendorTypes.find((type) => type.value === formData.vendorType);
  const passwordOk = formData.password.length >= 8;

  const renderStep1 = () => (
    <div>
      <div className="mb-6">
        <h1 className="font-['Manrope'] text-3xl font-extrabold tracking-tight text-gray-900">
          Create your account
        </h1>
        <p className="mt-2 text-[15px] text-gray-500">
          Free to join · no setup fees.
        </p>
      </div>

      {/* 1 — Vendor type */}
      <div className="mb-6">
        <SectionTitle n="1" title="What type of vendor are you?" />
        <div className="grid grid-cols-2 gap-2.5">
          {vendorTypes.map((type) => {
            const Icon = type.icon;
            const selected = formData.vendorType === type.value;
            return (
              <button
                type="button"
                key={type.value}
                onClick={() => handleInputChange("vendorType", type.value)}
                aria-pressed={selected}
                className={`relative flex items-center gap-3 rounded-2xl border p-3 text-left transition-all duration-200 ${
                  selected
                    ? "border-purple-400 bg-purple-50/70 ring-4 ring-purple-500/10"
                    : "border-gray-200 bg-white hover:border-purple-200 hover:bg-purple-50/30"
                }`}
              >
                <span
                  className={`flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl transition-all ${
                    selected
                      ? "bg-gradient-to-br from-purple-600 to-pink-600 text-white shadow-md shadow-purple-600/20"
                      : "bg-gray-100 text-gray-500"
                  }`}
                >
                  <Icon className="h-5 w-5" />
                </span>
                <span className="min-w-0">
                  <span className="block truncate font-['Manrope'] text-sm font-bold text-gray-900">
                    {type.label}
                  </span>
                  <span className="block truncate text-xs text-gray-500">
                    {type.desc}
                  </span>
                </span>
                {selected && (
                  <span className="absolute right-2 top-2 flex h-4 w-4 items-center justify-center rounded-full bg-purple-600 text-white">
                    <Check className="h-2.5 w-2.5" strokeWidth={3} />
                  </span>
                )}
              </button>
            );
          })}
        </div>
        {errors.vendorType && <p className={ERROR_TEXT}>{errors.vendorType}</p>}
      </div>

      {/* 2 — About the business */}
      <div className="mb-6">
        <SectionTitle n="2" title="About your business" />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className={labelClass}>Business name</label>
            <div className="group/field relative">
              <Building2 className={fieldIconClass} />
              <input
                type="text"
                value={formData.business_name}
                onChange={(e) => handleInputChange("business_name", e.target.value)}
                className={fieldClass(errors.business_name)}
                placeholder="Business name"
                autoComplete="organization"
              />
            </div>
            {errors.business_name && <p className={ERROR_TEXT}>{errors.business_name}</p>}
          </div>

          <div>
            <label className={labelClass}>Full name</label>
            <div className="group/field relative">
              <User className={fieldIconClass} />
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                className={fieldClass(errors.name)}
                placeholder="Your name"
                autoComplete="name"
              />
            </div>
            {errors.name && <p className={ERROR_TEXT}>{errors.name}</p>}
          </div>
        </div>
      </div>

      {/* 3 — Sign-in details */}
      <div>
        <SectionTitle n="3" title="Sign-in details" />
        <div className="space-y-4">
          <div>
            <label className={labelClass}>Email address</label>
            <div className="group/field relative">
              <Mail className={fieldIconClass} />
              <input
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange("email", e.target.value)}
                className={fieldClass(errors.email)}
                placeholder="you@business.com"
                autoComplete="email"
              />
            </div>
            {errors.email && <p className={ERROR_TEXT}>{errors.email}</p>}
          </div>

          <div>
            <label className={labelClass}>Password</label>
            <div className="group/field relative">
              <Lock className={fieldIconClass} />
              <input
                type={showPassword ? "text" : "password"}
                value={formData.password}
                onChange={(e) => handleInputChange("password", e.target.value)}
                className={`${fieldClass(errors.password)} !pr-12`}
                placeholder="At least 8 characters"
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 transition-colors hover:text-gray-600"
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <EyeOff className="h-[18px] w-[18px]" /> : <Eye className="h-[18px] w-[18px]" />}
              </button>
            </div>
            {errors.password ? (
              <p className={ERROR_TEXT}>{errors.password}</p>
            ) : (
              <p className={`mt-2 inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${
                passwordOk ? "border-green-200 bg-green-50 text-green-700" : "border-gray-200 bg-gray-50 text-gray-500"
              }`}>
                <span className={`flex h-3.5 w-3.5 items-center justify-center rounded-full ${passwordOk ? "bg-green-500 text-white" : "bg-gray-300 text-white"}`}>
                  <Check className="h-2.5 w-2.5" strokeWidth={3} />
                </span>
                At least 8 characters
              </p>
            )}
          </div>
        </div>
      </div>

      <button onClick={handleContinue} className={`${PRIMARY_BTN} mt-6`}>
        <span>Continue</span>
        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
      </button>

      <p className="mt-5 text-center text-sm text-gray-500">
        Already have an account?{" "}
        <button type="button" onClick={() => navigate("/vendor-signin")} className={LINK}>
          Sign in
        </button>
      </p>
    </div>
  );

  const renderStep2 = () => (
    <div>
      <div className="mb-6">
        <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-purple-600 to-pink-600 shadow-md shadow-purple-600/20">
          {otpSent ? <KeyRound className="h-6 w-6 text-white" /> : <Phone className="h-6 w-6 text-white" />}
        </div>
        <h1 className="font-['Manrope'] text-3xl font-extrabold tracking-tight text-gray-900">
          Verify your phone
        </h1>
        <p className="mt-2 text-[15px] text-gray-500">
          {otpSent ? (
            <>
              Code sent to{" "}
              <strong className="font-semibold text-gray-900">+91 {formData.phone}</strong>
            </>
          ) : (
            "We'll send a 6-digit code to your mobile number."
          )}
        </p>
      </div>

      {/* Who is registering — from step 1 */}
      <div className="mb-5 flex items-center gap-3 rounded-2xl border border-gray-200/70 bg-gray-50/70 p-3">
        <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-white text-purple-600 ring-1 ring-inset ring-gray-200/70">
          {selectedType ? <selectedType.icon className="h-5 w-5" /> : <Building2 className="h-5 w-5" />}
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-gray-900">{formData.business_name}</p>
          <p className="truncate text-xs text-gray-500">
            {selectedType?.label} · {formData.email}
          </p>
        </div>
      </div>

      <div className="space-y-5">
        {/* Mobile number */}
        <div>
          <label className={labelClass}>Mobile number</label>
          <div className="group/field relative">
            <Phone className={fieldIconClass} />
            <span className="pointer-events-none absolute left-11 top-1/2 -translate-y-1/2 border-r border-gray-200 pr-2.5 text-[15px] font-semibold text-gray-700">
              +91
            </span>
            <input
              type="tel"
              value={formData.phone}
              autoFocus={!otpSent}
              disabled={otpSent}
              onChange={(e) =>
                handleInputChange("phone", e.target.value.replace(/\D/g, "").slice(0, 10))
              }
              className={`${fieldClass(errors.phone)} !pl-[5.75rem] tracking-wide disabled:cursor-not-allowed disabled:text-gray-500`}
              placeholder="10-digit mobile"
              autoComplete="tel-national"
            />
            {otpSent && (
              <span className="absolute right-3.5 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-full bg-green-500 text-white">
                <Check className="h-3 w-3" strokeWidth={3} />
              </span>
            )}
          </div>
          {errors.phone && <p className={ERROR_TEXT}>{errors.phone}</p>}
        </div>

        {/* OTP — six boxes */}
        {otpSent && (
          <div>
            <label className={labelClass}>Enter the 6-digit code</label>
            <div
              className="grid grid-cols-6 gap-2"
              onPaste={(e) => {
                e.preventDefault();
                handleOtpInput(0, e.clipboardData.getData("text"));
              }}
            >
              {Array.from({ length: 6 }).map((_, i) => (
                <input
                  key={i}
                  ref={(el) => (otpRefs.current[i] = el)}
                  type="text"
                  inputMode="numeric"
                  autoComplete={i === 0 ? "one-time-code" : "off"}
                  aria-label={`Digit ${i + 1}`}
                  autoFocus={i === 0}
                  maxLength={6}
                  value={otp[i] || ""}
                  onChange={(e) => handleOtpInput(i, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(i, e)}
                  onFocus={(e) => e.target.select()}
                  className={`h-14 w-full rounded-xl border text-center font-['Manrope'] text-xl font-extrabold text-gray-900 tabular-nums transition-all focus:bg-white focus:outline-none focus:ring-4 ${
                    errors.otp
                      ? "border-red-400 bg-red-50/40 focus:border-red-500 focus:ring-red-500/10"
                      : otp[i]
                        ? "border-purple-300 bg-purple-50/50 focus:border-purple-500 focus:ring-purple-500/10"
                        : "border-gray-200 bg-gray-50/70 focus:border-purple-500 focus:ring-purple-500/10"
                  }`}
                />
              ))}
            </div>
            {errors.otp && <p className={ERROR_TEXT}>{errors.otp}</p>}
          </div>
        )}
      </div>

      {errors.submit && (
        <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-3.5">
          <p className="text-sm text-red-600">{errors.submit}</p>
        </div>
      )}

      <button
        onClick={otpSent ? handleVerifyOtp : handleSendOtp}
        disabled={loading || (otpSent && otp.length !== 6)}
        className={`${PRIMARY_BTN} mt-6`}
      >
        {loading ? (
          <>
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
            <span>{otpSent ? "Verifying…" : "Sending OTP…"}</span>
          </>
        ) : (
          <>
            <span>{otpSent ? "Verify & continue" : "Send OTP"}</span>
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </>
        )}
      </button>

      <div className="mt-4 flex items-center justify-between gap-3">
        <button
          onClick={() => {
            setOtpSent(false);
            setOtp("");
            setErrors({});
            setStep(1);
          }}
          className="flex h-9 items-center gap-1.5 rounded-lg px-2.5 text-xs font-semibold text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-900"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back to details
        </button>
        {otpSent && (
          <p className="text-xs text-gray-500">
            Didn&apos;t get the code?{" "}
            <button onClick={handleResendOtp} disabled={loading} className={`${LINK} disabled:opacity-50`}>
              {loading ? "Sending…" : "Resend"}
            </button>
          </p>
        )}
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="text-center">
      <div className="relative mx-auto mb-5 h-20 w-20">
        <div className="absolute inset-0 rounded-3xl bg-green-200/60 blur-xl" />
        <div className="relative flex h-20 w-20 items-center justify-center rounded-3xl border border-green-200 bg-green-50">
          <CheckCircle className="h-10 w-10 text-green-600" />
        </div>
      </div>
      <h1 className="font-['Manrope'] text-3xl font-extrabold tracking-tight text-gray-900">
        You&apos;re all set
      </h1>
      <p className="mx-auto mt-2 max-w-xs text-[15px] text-gray-500">
        Account created and verified. Sign in to complete your profile and start
        receiving bookings.
      </p>

      {/* What happens next */}
      <ol className="mt-6 space-y-2 text-left">
        {[
          { icon: Lock, title: "Sign in" },
          { icon: Building2, title: "Complete your profile" },
          { icon: CheckCircle, title: "Start receiving bookings" },
        ].map((item, i) => (
          <li key={item.title} className="flex items-center gap-3 rounded-xl border border-gray-200/70 bg-gray-50/70 p-3">
            <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-white text-purple-600 ring-1 ring-inset ring-gray-200/70">
              <item.icon className="h-[18px] w-[18px]" />
            </span>
            <span className="text-sm font-semibold text-gray-900">{item.title}</span>
            <span className="ml-auto font-['Manrope'] text-xs font-bold text-gray-300 tabular-nums">0{i + 1}</span>
          </li>
        ))}
      </ol>

      <button onClick={() => navigate("/vendor-signin")} className={`${PRIMARY_BTN} mt-6`}>
        <span>Go to sign in</span>
        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
      </button>
    </div>
  );

  return (
    <AuthLayout>
      <Stepper />
      {step === 1 && renderStep1()}
      {step === 2 && renderStep2()}
      {step === 3 && renderStep3()}
    </AuthLayout>
  );
};

export default VendorRegistration;
